import { expensesApis } from "../../api/Expenses";

export interface ItemizedExpenseItem {
  category: string;
  description: string;
  amount: number;
  tax_percentage: string;
  hsnSac: string;
}

export interface ReceiptOCRData {
  merchant?: string;
  amount?: string | number;
  subtotal?: string | number;
  totalTax?: string | number;
  date?: string;
  gstin?: string;
  taxAmount?: string;
  tax_percentage?: string | number;
  reference?: string;
  referenceId?: string;
  description?: string;
  category?: string;
  hsnSac?: string;
  hsnCode?: string;
  sacCode?: string;
  paymentMode?: string;
  destinationOfSupply?: string;
  destinationOfSupply3?: string;
  productCategory?: 'Goods' | 'Service';
  itemizedExpenses?: ItemizedExpenseItem[];
  items?: Array<{
    category: string;
    itemName: string;
    amount: string | number;
    description: string;
    tax_percentage: number;
    hsnCode?: string;
    sacCode?: string;
  }>;
  hasItemizedData?: boolean;
  actionDate?: string;
  taxType?: string;
  currency?: string;
  currencyId?: number;
  claimReimbursement?: boolean;
}

// Helper function to check if file is OCR-processable
const isOCRProcessableFile = (file: File): boolean => {
  const fileType = file.type.toLowerCase();
  const fileName = file.name.toLowerCase();
  
  // Check by MIME type
  if (fileType.startsWith('image/')) return true;
  if (fileType === 'application/pdf') return true;
  if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return true; // .docx
  if (fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return true; // .xlsx
  if (fileType === 'application/vnd.ms-excel') return true; // .xls
  if (fileType === 'text/csv') return true; // .csv
  
  // Fallback: check by file extension
  const validExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.docx', '.xlsx', '.xls', '.csv'];
  return validExtensions.some(ext => fileName.endsWith(ext));
};

export const extractReceiptData = async (file: File, paymentId: string | number): Promise<ReceiptOCRData | null> => {
  try {
    // Process all supported OCR file types
    if (!isOCRProcessableFile(file)) {
      return null;
    }

    const response = await expensesApis.processReceiptOCR(file, paymentId);
    const responseData = response?.data || response;
    
    let apiData;
    if (responseData && responseData.status !== undefined) {
      if (!responseData.status) {
        return null;
      }
      apiData = responseData.data || responseData;
    } else {
      apiData = responseData;
    }

    if (!apiData) {
      return null;
    }

    // Map new API response format (snake_case) to camelCase
    // Support both old format (camelCase) and new format (snake_case)
    const expenseDate = apiData.expense_date || apiData.actionDate || '';
    const vendorSupplier = apiData.vendor_supplier || apiData.merchant || '';
    const taxType = apiData.tax_type || apiData.taxType || 'GST Not Applicable';
    const productCategory = apiData.product_category || apiData.productCategory || 'Service';
    const paymentMode = apiData.payment_mode || apiData.paymentMode || '';
    const referenceNumber = apiData.reference_number || apiData.referenceId || apiData.reference || '';
    const destinationOfSupply = apiData.destination_of_supply || apiData.destinationOfSupply || '';
    
    // Get amount from totals if available, otherwise use direct amount (PDF total / final amount)
    const totalAmount = apiData.totals?.total_amount || apiData.amount;
    const amount = totalAmount !== undefined && totalAmount !== null 
      ? (typeof totalAmount === 'number' ? totalAmount.toString() : totalAmount) 
      : '';
    const subtotal = apiData.totals?.subtotal ?? apiData.totals?.sub_total ?? apiData.subtotal;
    const totalTax = apiData.totals?.total_tax ?? apiData.total_tax ?? apiData.totalTax;

    // Format date - handle DD-MM-YYYY format from new API
    let formattedDate = '';
    let actionDate = '';
    if (expenseDate) {
      // Try to parse DD-MM-YYYY format
      const dateMatch = expenseDate.match(/(\d{2})-(\d{2})-(\d{4})/);
      if (dateMatch) {
        const [, day, month, year] = dateMatch;
        const dateObj = new Date(`${year}-${month}-${day}`);
        if (!isNaN(dateObj.getTime())) {
          formattedDate = formatApiDate(dateObj.toISOString());
          actionDate = dateObj.toISOString();
        }
      } else {
        // Try standard date parsing
        formattedDate = formatApiDate(expenseDate);
        actionDate = expenseDate;
      }
    }

    const ocrData: ReceiptOCRData = {
      merchant: vendorSupplier,
      amount: amount,
      subtotal: subtotal !== undefined && subtotal !== null ? (typeof subtotal === 'number' ? subtotal : parseFloat(subtotal) || subtotal) : undefined,
      totalTax: totalTax !== undefined && totalTax !== null ? (typeof totalTax === 'number' ? totalTax : parseFloat(totalTax) || totalTax) : undefined,
      date: formattedDate,
      gstin: apiData.gstin || '',
      tax_percentage: apiData.tax_percentage || apiData.tax_percent || apiData.taxPercent !== undefined && apiData.taxPercent !== null
        ? (typeof (apiData.tax_percentage || apiData.taxPercent) === 'number' ? (apiData.tax_percentage || apiData.taxPercent).toString() : (apiData.tax_percentage || apiData.taxPercent))
        : '',
      reference: referenceNumber,
      referenceId: referenceNumber,
      description: apiData.description || '',
      category: apiData.category || '',
      hsnSac: apiData.hsn_code || apiData.sac_code || apiData.hsn_sac_code || apiData.hsnCode || apiData.sacCode || apiData.hsnSac || '',
      hsnCode: apiData.hsn_code || apiData.hsn_sac_code || apiData.hsnCode || '',
      sacCode: apiData.sac_code || apiData.hsn_sac_code || apiData.sacCode || '',
      paymentMode: paymentMode,
      destinationOfSupply: destinationOfSupply,
      destinationOfSupply3: destinationOfSupply,
      productCategory: productCategory === 'Goods' || productCategory === 'Service' ? productCategory : 'Service',
      taxType: taxType,
      currency: apiData.currency || 'INR',
      currencyId: apiData.currency_id || apiData.currencyId || 1,
      claimReimbursement: apiData.claim_reimbursement !== undefined ? apiData.claim_reimbursement : (apiData.claimReimbursement !== undefined ? apiData.claimReimbursement : false),
      actionDate: actionDate,
    };

    // Process items array - map new format to expected format
    if (apiData.items && Array.isArray(apiData.items) && apiData.items.length > 0) {
      ocrData.hasItemizedData = true;
      
      // Transform items from new API format to expected format
      const transformedItems = apiData.items.map((item: any) => {
        // Calculate taxPercent from gst_amount or tax_percentage
        let taxPercent = 0;
        if (item.tax_percentage !== undefined) {
          taxPercent = typeof item.tax_percentage === 'string' ? parseFloat(item.tax_percentage) : item.tax_percentage || 0;
        } else if (item.gst_amount) {
          const cgst = item.gst_amount.cgst || 0;
          const sgst = item.gst_amount.sgst || 0;
          const totalGst = cgst + sgst;
          const itemAmount = typeof item.amount === 'string' ? parseFloat(item.amount) : item.amount || 0;
          if (itemAmount > 0 && totalGst > 0) {
            taxPercent = Math.round(((totalGst / itemAmount) * 100) * 100) / 100; // Round to 2 decimal places
          }
        } else if (item.tax_percent !== undefined) {
          taxPercent = typeof item.tax_percent === 'string' ? parseFloat(item.tax_percent) : item.tax_percent || 0;
        } else if (item.taxPercent !== undefined) {
          taxPercent = typeof item.taxPercent === 'string' ? parseFloat(item.taxPercent) : item.taxPercent || 0;
        }

        // Determine hsnCode or sacCode based on productCategory
        const hsnSacCode = item.hsn_sac_code || item.hsnCode || item.sacCode || '';
        const isGoods = productCategory === 'Goods';
        
        return {
          category: item.category_name || item.category || '',
          itemName: item.item_name || item.itemName || '',
          amount: typeof item.amount === 'string' ? item.amount : item.amount.toString(),
          description: item.description || '',
          tax_percentage: taxPercent,
          hsnCode: isGoods ? hsnSacCode : undefined,
          sacCode: !isGoods ? hsnSacCode : undefined,
        };
      });
      
      ocrData.items = transformedItems;
      ocrData.itemizedExpenses = transformedItems.map((item: any) => ({
        category: item.category || '',
        description: item.description || '',
        amount: typeof item.amount === 'string' ? parseFloat(item.amount) || 0 : item.amount || 0,
        tax_percentage: item.tax_percentage ? `${item.tax_percentage.toFixed(2)}%` : '0%',
        hsnSac: item.hsnCode || item.sacCode || ''
      }));
    } else {
      ocrData.hasItemizedData = false;
      ocrData.itemizedExpenses = undefined;
      ocrData.items = undefined;
    }

    // Set totalTax from totals if not already set
    if (totalTax !== undefined && totalTax !== null && ocrData.totalTax == null) {
      ocrData.totalTax = typeof totalTax === 'number' ? totalTax : parseFloat(totalTax) || totalTax;
    }
    // Calculate taxAmount from totals if available
    if (apiData.totals?.total_tax) {
      ocrData.taxAmount = typeof apiData.totals.total_tax === 'number' 
        ? apiData.totals.total_tax.toFixed(2) 
        : apiData.totals.total_tax;
      if (ocrData.totalTax == null) ocrData.totalTax = typeof apiData.totals.total_tax === 'number' ? apiData.totals.total_tax : parseFloat(apiData.totals.total_tax);
    } else if (ocrData.amount && ocrData.tax_percentage) {
      const amount = typeof ocrData.amount === 'string' ? parseFloat(ocrData.amount) : ocrData.amount;
      const taxPercent = typeof ocrData.tax_percentage === 'string' ? parseFloat(ocrData.tax_percentage) : ocrData.tax_percentage;
      if (amount && taxPercent) {
        const taxAmount = (amount * taxPercent) / 100;
        ocrData.taxAmount = taxAmount.toFixed(2);
      }
    }

    return ocrData;
  } catch (error) {
    return null;
  }
};

const formatApiDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '';
    }
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (error) {
    return '';
  }
};

